import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import API from "../../../util/api";
import {
  adminTable,
  adminTableHeaderCell,
  adminTableDataCell,
} from "../common/AdminTableStyles";

const breakpoints = {
  tablet: 768,
  phone:  480,
};

// new wrapper to constrain width and allow inner scrolling
const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  box-sizing: border-box;
`;

const FilterBar = styled.div`
  margin-bottom: 1em;
  font-size: 0.95em;
  strong {
    margin-right: 0.5em;
  }
  select {
    padding: 4px 8px;
    border: 1px solid ${({ colors }) => colors.border};
    border-radius: 4px;
    font-size: 0.95em;
  }
`;

const Table = styled.table`
  ${adminTable};
  min-width: 600px;
  font-size: 0.95em;

  thead th {
    ${adminTableHeaderCell};
    padding: 8px;
  }

  tbody td {
    ${adminTableDataCell};
    padding: 8px;
  }
`;

const ActionButton = styled.button`
  background: ${({ colors }) => colors.buttonBackground};
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  font-size: 0.85em;

  &:hover {
    opacity: 0.9;
  }
`;

const spin = keyframes`
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingOverlay = styled.div`
  text-align: center;
  margin-top: 1em;
`;

const Spinner = styled.div`
  display: inline-block;
  width: 24px;
  height: 24px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid ${({ colors }) => colors.buttonBackground};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const RESOURCE_TYPES = [
  { id: 1, label: "Audio" },
  { id: 2, label: "Document" },
  { id: 3, label: "Image" },
  { id: 4, label: "Package" },
  { id: 5, label: "Video" },
];

export default function ResourcesList({ colors }) {
  const [filterType, setFilterType] = useState(5);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);

  const typeLabel =
    RESOURCE_TYPES?.find((rt) => rt.id === filterType)?.label.toLowerCase();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = filterType === 0 ? {} : { type: typeLabel };
        const resp = await API.getResources(params);
        setResources(
          resp.status === "Success" && Array.isArray(resp.result)
            ? resp.result
            : []
        );
      } catch {
        setResources([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [filterType, typeLabel]);

  const handleDownload = (r) => {
    const link = document.createElement("a");
    link.href = r.file;
    const name = r.filename || r.resource_name;
    const ext = r.extension ? `.${r.extension}` : "";
    link.setAttribute("download", `${name}${ext}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const filtered = resources.filter((r) =>
    filterType === 0 ? true : r.type === typeLabel
  );

  return (
    <>
      <FilterBar colors={colors}>
        <strong>Filter:</strong>
        <select
          value={filterType}
          onChange={(e) => setFilterType(Number(e.target.value))}
        >
          {RESOURCE_TYPES?.map((rt) => (
            <option key={rt.id} value={rt.id}>
              {rt.label}
            </option>
          ))}
        </select>
      </FilterBar>

      <TableWrapper>
        <Table colors={colors}>
          <thead>
            <tr>
              <th>Resource</th>
              <th>Type</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && !loading ? (
              <tr>
                <td colSpan="4">
                  There are no resources available at this time.
                </td>
              </tr>
            ) : (
              filtered?.map((r) => (
                <tr key={r.resource_id}>
                  <td>{r.resource_name}</td>
                  <td>
                    {
                      RESOURCE_TYPES?.find(
                        (rt) => rt.label.toLowerCase() === r.type
                      )?.label || r.type
                    }
                  </td>
                  <td>{r.resource_description || "—"}</td>
                  <td>
                    <ActionButton
                      colors={colors}
                      onClick={() => handleDownload(r)}
                    >
                      Download
                    </ActionButton>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </TableWrapper>

      {loading && (
        <LoadingOverlay>
          <Spinner colors={colors} />
        </LoadingOverlay>
      )}
    </>
  );
}
